import { Button } from '../Button';
import { FieldWrapper } from '../FieldWrapper';
import { Label } from '../Label';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../Select';
import { DateRangePickerCalendar } from './components/DateRangePickerCalendar';
import { DateRangePickerInputs } from './components/DateRangePickerInputs';
import { DEFAULT_PREDEFINED_RANGES } from './constants';
import { useDateRangePicker } from './hooks/useDateRangePicker';
import type { DateRangePickerProps } from './types';

function DateRangePicker(props: DateRangePickerProps) {
  const {
    fromDate,
    toDate,
    onDateChange,
    predefinedRanges = DEFAULT_PREDEFINED_RANGES,
    disabled = false,
    disableFutureDays = false,
    renderTrigger,
    label,
    asChild = false,
  } = props;

  const {
    isOpen,
    setIsOpen,
    displayText,
    selectedRangeType,
    onPredefinedRangeChange,
    fromInputControl,
    toInputControl,
    fromError,
    toError,
    calendarState,
  } = useDateRangePicker({
    fromDate,
    toDate,
    onDateChange,
    disabled,
    disableFutureDays,
  });

  const content = (
    <div
      className={`bg-bg-primary flex flex-col gap-3 ${
        asChild ? '' : 'shadow-lg p-xl'
      }`}
    >
      <FieldWrapper>
        {!asChild && (
          <Label htmlFor="date-range-picker-select">Show {label} for</Label>
        )}
        <Select
          value={selectedRangeType}
          onValueChange={onPredefinedRangeChange}
          disabled={disabled}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a range" />
          </SelectTrigger>
          <SelectContent>
            {predefinedRanges.map((range) => (
              <SelectItem key={range.value} value={range.value}>
                {range.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldWrapper>

      <DateRangePickerInputs
        fromInputControl={fromInputControl}
        toInputControl={toInputControl}
        fromError={fromError}
        toError={toError}
        disabled={disabled}
      />

      <div className="mt-1">
        <DateRangePickerCalendar
          calendarState={calendarState}
          disabled={disabled}
          disableFutureDays={disableFutureDays}
        />
      </div>
    </div>
  );

  if (asChild) {
    return (
      <>
        <style>{`
          @keyframes slideInFromRight {
            from {
              transform: translateX(100%);
            }
            to {
              transform: translateX(0);
            }
          }
          @keyframes slideInFromLeft {
            from {
              transform: translateX(-100%);
            }
            to {
              transform: translateX(0);
            }
          }
          @keyframes slideOutToLeft {
            from {
              transform: translateX(0);
            }
            to {
              transform: translateX(-100%);
            }
          }
          @keyframes slideOutToRight {
            from {
              transform: translateX(0);
            }
            to {
              transform: translateX(100%);
            }
          }
        `}</style>
        {content}
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes slideInFromRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        @keyframes slideInFromLeft {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }
        @keyframes slideOutToLeft {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-100%);
          }
        }
        @keyframes slideOutToRight {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(100%);
          }
        }
      `}</style>
      <Popover modal open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild disabled={disabled}>
          {renderTrigger ? (
            renderTrigger({
              fromDate,
              toDate,
              isOpen,
              onClick: () => setIsOpen(true),
            })
          ) : (
            <Button
              variant="secondary"
              className="w-full justify-start"
              disabled={disabled}
            >
              {displayText}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 max-w-[360px] overflow-clip"
          align="start"
          sideOffset={4}
          avoidCollisions
        >
          {content}
        </PopoverContent>
      </Popover>
    </>
  );
}

DateRangePicker.displayName = 'DateRangePicker';

export { DateRangePicker };
